import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Building2,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing invite token');
      setLoading(false);
      return;
    }
    
    // Validate token (public endpoint)
    fetch(`${BACKEND_URL}/api/team/invite/validate/${token}`)
      .then(res => {
        if (!res.ok) return res.json().then(e => { throw new Error(e.detail) });
        return res.json();
      })
      .then(data => {
        setInviteData(data);
      })
      .catch(err => {
        setError(err.message || 'Invalid invite link');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const handleAccept = async () => {
    if (!isSignedIn) {
      // Redirect to sign-in, preserving return URL
      // Clerk handles redirect automatically if configured, or we can force it
      // For now, assume user needs to sign in/up first
      window.location.href = `/sign-up?redirect_url=${encodeURIComponent(window.location.href)}`;
      return;
    }

    setAccepting(true);
    try {
      const authToken = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/team/accept?token=${token}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to accept invite');
      }
      
      // Success - Redirect to dashboard
      // Force reload to refresh context/business
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.message);
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invite Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-blue-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Join {inviteData.business_name}</h1>
        <p className="text-gray-600 mb-6">
          You have been invited to join <strong>{inviteData.business_name}</strong> as a <strong>{inviteData.role}</strong>.
        </p>
        
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Invite for</p>
          <p className="text-gray-900 font-medium">{inviteData.email}</p>
        </div>

        <button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {accepting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Joining Team...
            </>
          ) : (
            <>
              Accept Invite
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
        
        {!isSignedIn && (
          <p className="text-sm text-gray-500 mt-4">
            You'll be asked to sign in or create an account.
          </p>
        )}
      </div>
    </div>
  );
}
